<!--
  @component ItemsPanel
  @description Center panel displaying items list with sorting and selection
-->
<script lang="ts">
  import { createEventDispatcher } from 'svelte';
  import { t } from '@/i18n';
  import {
    items,
    selectedItem,
    selectedItemKeys,
    isItemsLoading,
    totalItems,
    sortState,
    searchQuery,
    isSearchActive,
    selectItem,
    toggleItemSelection,
    selectRange,
    addToSelection,
    loadMoreItems,
    hasMoreItems,
  } from '@stores/itemStore';
  import {
    selectedCollectionKey,
    selectedCollection,
    isSpecialCollection,
  } from '@stores/collectionStore';
  import { ITEM_TYPES } from '@/data';
  import type { Item, ItemSortColumn } from '@types';

  const dispatch = createEventDispatcher<{
    selectItem: { item: Item };
    editItem: { item: Item };
    itemContextMenu: { x: number; y: number; item: Item };
    sort: { column: ItemSortColumn };
  }>();

  // Track last clicked item for shift+click range selection
  let lastClickedKey: string | null = null;

  // Column definitions
  const columns: { key: ItemSortColumn; label: string; width: string }[] = [
    { key: 'title', label: 'item.fields.title', width: '1fr' },
    { key: 'creator', label: 'item.fields.creators', width: '150px' },
    { key: 'dateModified', label: 'item.dateModified', width: '100px' },
  ];

  // Infinite scroll
  let isLoadingMore = false;

  function handleScroll(event: Event) {
    const target = event.target as HTMLElement;
    if (!target) return;

    const { scrollTop, scrollHeight, clientHeight } = target;
    const scrollThreshold = 200; // Load more when within 200px of bottom
    const distanceToBottom = scrollHeight - scrollTop - clientHeight;

    if (!$hasMoreItems || isLoadingMore || $isItemsLoading) return;

    if (distanceToBottom <= scrollThreshold) {
      isLoadingMore = true;
      loadMoreItems().finally(() => {
        isLoadingMore = false;
      });
    }
  }

  // Get item type icon from centralized ITEM_TYPES
  function getItemIcon(itemType: string): string {
    return ITEM_TYPES[itemType]?.icon || '📄';
  }

  // Get first creator name
  function getCreatorName(item: Item): string {
    const creator = item.data.creators?.[0];
    if (!creator) return '';
    return creator.lastName || creator.name || '';
  }

  // Format date
  function formatDate(dateString: string | undefined): string {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString();
  }

  // Event handlers
  function handleItemClick(event: MouseEvent, item: Item) {
    const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
    const isModifierKey = isMac ? event.metaKey : event.ctrlKey;

    if (event.shiftKey && lastClickedKey) {
      // Shift+click: select range
      selectRange(lastClickedKey, item.key);
    } else if (isModifierKey) {
      // Cmd/Ctrl+click: toggle selection
      if ($selectedItem?.key === item.key) {
        // If clicking the primary selected item with modifier, add to multi-select
        addToSelection(item.key);
      } else {
        toggleItemSelection(item.key);
      }
    } else {
      // Normal click: single select
      selectItem(item);
      dispatch('selectItem', { item });
    }

    lastClickedKey = item.key;
  }

  function handleItemDoubleClick(item: Item) {
    dispatch('editItem', { item });
  }

  function handleItemContextMenu(event: MouseEvent, item: Item) {
    event.preventDefault();
    event.stopPropagation();

    // If right-clicking on an item that's NOT in the current selection,
    // select only that item (like most file managers do)
    const isInSelection = $selectedItemKeys.has(item.key) || $selectedItem?.key === item.key;
    if (!isInSelection) {
      selectItem(item);
    }

    dispatch('itemContextMenu', {
      x: event.clientX,
      y: event.clientY,
      item,
    });
  }

  function handleSort(column: ItemSortColumn) {
    dispatch('sort', { column });
  }

  // Get collection display name
  $: collectionName = $selectedCollectionKey
    ? isSpecialCollection($selectedCollectionKey)
      ? $t(`collection.${$selectedCollectionKey === 'all' ? 'allItems' : $selectedCollectionKey}`)
      : $selectedCollection?.data?.name || ''
    : '';
</script>

<div class="items-panel">
  <!-- Header -->
  <div class="items-header">
    <span class="header-title">{collectionName || $t('collection.allItems')}</span>
    <span class="header-count">
      {#if $isItemsLoading}
        <span class="loading-spinner"></span>
      {:else}
        {$totalItems || $items.length}
        {$t('item.items')}
        {#if $isSearchActive}
          <span class="filtered-badge">({$t('search.searching')})</span>
        {/if}
      {/if}
    </span>
  </div>

  <!-- Column Headers -->
  {#if $items.length > 0}
    <div class="items-list-header">
      <span class="header-icon"></span>
      {#each columns as column}
        <button
          class="sortable-header"
          class:active={$sortState.column === column.key}
          on:click={() => handleSort(column.key)}
          style="width: {column.width};"
        >
          {$t(column.label)}
          {#if $sortState.column === column.key}
            <span class="sort-indicator">
              {$sortState.direction === 'asc' ? '↑' : '↓'}
            </span>
          {/if}
        </button>
      {/each}
    </div>
  {/if}

  <!-- Items List -->
  <div class="items-list" on:scroll={handleScroll}>
    {#if $items.length > 0}
      {#each $items as item (item.key)}
        <button
          class="item-row"
          class:selected={$selectedItem?.key === item.key || $selectedItemKeys.has(item.key)}
          on:click={(e) => handleItemClick(e, item)}
          on:dblclick={() => handleItemDoubleClick(item)}
          on:contextmenu={(e) => handleItemContextMenu(e, item)}
        >
          <span class="item-type-icon">{getItemIcon(item.data.itemType)}</span>
          <div class="item-title">{item.data.title || $t('item.noItems')}</div>
          <div class="item-creator">{getCreatorName(item)}</div>
          <div class="item-date">{formatDate(item.data.dateModified)}</div>
        </button>
      {/each}
      <!-- Loading more indicator -->
      {#if $hasMoreItems}
        <div class="load-more-indicator">
          {#if isLoadingMore || $isItemsLoading}
            <span class="loading-spinner"></span>
            <span>{$t('common.loading')}</span>
          {:else}
            <span class="items-count">{$items.length} / {$totalItems}</span>
          {/if}
        </div>
      {/if}
    {:else if $isSearchActive}
      <div class="empty-state">
        <div class="empty-icon">🔍</div>
        <div class="empty-text">{$t('search.noResults')}</div>
      </div>
    {:else if $selectedCollectionKey}
      <div class="empty-state">
        <div class="empty-icon">📚</div>
        <div class="empty-text">{$t('item.noItems')}</div>
      </div>
    {:else}
      <div class="empty-state">
        <div class="empty-icon">👈</div>
        <div class="empty-text">{$t('detailsPanel.selectItemToView')}</div>
      </div>
    {/if}
  </div>
</div>

<style>
  .items-panel {
    flex: 1;
    background: var(--color-bg-primary, #2b2b2b);
    display: flex;
    flex-direction: column;
    min-width: 300px;
  }

  .items-header {
    padding: 12px;
    background: var(--color-bg-tertiary, #3c3c3c);
    border-bottom: 1px solid var(--color-border, #555);
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-shrink: 0;
  }

  .header-title {
    font-weight: 600;
    font-size: 13px;
  }

  .header-count {
    font-size: 12px;
    color: var(--color-text-secondary, #aaa);
    display: flex;
    align-items: center;
    gap: 4px;
  }

  .filtered-badge {
    color: var(--color-accent, #5c7cfa);
    font-style: italic;
  }

  .items-list-header {
    display: grid;
    grid-template-columns: 30px 1fr 150px 100px;
    gap: 12px;
    padding: 8px 12px;
    background: var(--color-bg-tertiary, #3c3c3c);
    border-bottom: 1px solid var(--color-border, #555);
    font-size: 12px;
    font-weight: 600;
    color: var(--color-text-secondary, #bbb);
    position: sticky;
    top: 0;
    z-index: 5;
    flex-shrink: 0;
  }

  .header-icon {
    width: 30px;
  }

  .sortable-header {
    cursor: pointer;
    user-select: none;
    display: flex;
    align-items: center;
    gap: 4px;
    transition: color 0.2s;
    background: none;
    border: none;
    color: var(--color-text-secondary, #bbb);
    font-size: 12px;
    font-weight: 600;
    padding: 0;
    text-align: left;
  }

  .sortable-header:hover {
    color: var(--color-text-primary, #e8e8e8);
  }

  .sortable-header.active {
    color: var(--color-accent, #5c7cfa);
  }

  .sort-indicator {
    font-size: 10px;
    opacity: 0.8;
  }

  .items-list {
    flex: 1;
    overflow-y: auto;
  }

  .item-row {
    display: grid;
    grid-template-columns: 30px 1fr 150px 100px;
    gap: 12px;
    padding: 10px 12px;
    border: none;
    border-bottom: 1px solid var(--color-border-light, #444);
    cursor: pointer;
    font-size: 13px;
    align-items: center;
    transition: background-color 0.2s;
    background: transparent;
    color: var(--color-text-primary, #e8e8e8);
    width: 100%;
    text-align: left;
  }

  .item-row:hover {
    background: var(--color-bg-hover, #333);
  }

  .item-row.selected {
    background: var(--color-bg-selected, #4a4a4a);
  }

  .item-type-icon {
    font-size: 16px;
    text-align: center;
  }

  .item-title {
    font-weight: 500;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .item-creator {
    color: var(--color-text-secondary, #aaa);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .item-date {
    color: var(--color-text-secondary, #aaa);
    text-align: right;
    font-size: 12px;
  }

  .empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 100%;
    color: var(--color-text-muted, #888);
    font-size: 14px;
    gap: 8px;
    padding: 40px;
  }

  .empty-icon {
    font-size: 48px;
    opacity: 0.5;
  }

  .empty-text {
    text-align: center;
    max-width: 300px;
    line-height: 1.4;
  }

  .loading-spinner {
    display: inline-block;
    width: 14px;
    height: 14px;
    border: 2px solid var(--color-border, #555);
    border-radius: 50%;
    border-top-color: var(--color-accent, #5c7cfa);
    animation: spin 1s ease-in-out infinite;
  }

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }

  /* ==========================================================================
     Responsive Styles
     ========================================================================== */

  /* Tablet: Hide date column */
  @media (max-width: 1023px) {
    .items-list-header {
      grid-template-columns: 30px 1fr 120px;
    }

    .item-row {
      grid-template-columns: 30px 1fr 120px;
    }

    .item-date {
      display: none;
    }
  }

  /* Mobile: Show only icon and title */
  @media (max-width: 767px) {
    .items-panel {
      min-width: 0;
    }

    .items-header {
      padding: 12px 16px;
    }

    .items-list-header {
      grid-template-columns: 30px 1fr;
      padding: 8px 16px;
    }

    .item-row {
      grid-template-columns: 30px 1fr;
      padding: 14px 16px;
      min-height: 56px;
    }

    .item-creator,
    .item-date {
      display: none;
    }

    .item-title {
      font-size: 14px;
      white-space: normal;
      overflow: hidden;
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      line-clamp: 2;
      line-height: 1.4;
    }

    .sortable-header:nth-child(n + 2) {
      display: none;
    }

    .header-icon {
      display: none;
    }

    .empty-state {
      padding: 24px 16px;
    }

    .empty-icon {
      font-size: 40px;
    }

    .empty-text {
      font-size: 14px;
    }
  }

  /* Touch-friendly targets */
  @media (pointer: coarse) {
    .item-row {
      min-height: 56px;
    }

    .sortable-header {
      min-height: 44px;
      padding: 8px 0;
    }
  }

  /* Load more indicator */
  .load-more-indicator {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 16px;
    color: var(--color-text-secondary, #aaa);
    font-size: 12px;
  }

  .items-count {
    color: var(--color-text-muted, #888);
  }
</style>
